import { formatBytes, formatDate } from "../../lib/format";
import { deriveGates, gateBlockers, gateCounts, qaRejected, qaVerified, type Gate, type GateCounts } from "../../lib/qa";
import type { RunStatus } from "../../lib/types";

/**
 * LIBRARY MODEL — pure, presentation-only derivations for /runs.
 *
 * CONSOLIDATION NOTE: `previewWords` and `plural` belong in lib/format.ts, and `nextStep`
 * belongs in lib/run.ts. They live here because this screen is being rebuilt concurrently with
 * nine others and a shared-lib edit is a merge hazard. Move them when the screens land.
 *
 * NOTHING HERE MAY INVENT A QA VERDICT. Every QA figure on this screen comes from
 * gateCounts(deriveGates(run, "idle")) and from nowhere else — never from run.status, never
 * from a floor, never as a percentage. approvalSummary()'s unqualified "Approved" label is
 * deliberately NOT used: on the screen where an operator picks the document to export, the word
 * "Approved" with no statement of whether the six checks ran is exactly the over-claim this
 * product exists to prevent.
 */

/** The first N words, verbatim, with NO ellipsis. The fade mask shows the continuation. */
export function previewWords(text: string, count: number) {
  const words = text.replace(/\s+/g, " ").trim().split(" ").filter(Boolean);
  return words.slice(0, count).join(" ");
}

/** "1 document" / "7 documents". The old screen printed "1 customer documents". */
export function plural(n: number, one: string, many: string) {
  return `${n} ${n === 1 ? one : many}`;
}

/** Sentence-case a backend token that has already had its underscores softened. */
export function sentenceCase(value: string) {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * The document excerpt: the first EDITABLE section's own words, verbatim.
 *
 * It ENDS ON A SENTENCE. The old cut ran to 60 words and stopped wherever it landed, which on
 * the rendered page left a paragraph hanging mid-clause ("...has operated since 2021 across 9
 * country nodes") — a page that appears to have been cut off, on a screen whose whole job is to
 * make the document trustworthy. So the cut is pulled back to the last full stop inside the
 * budget. If there is no sentence end in range the word cut stands: a shortened excerpt is
 * honest, and the sans note beneath it says so. Nothing is ever appended, and no ellipsis is
 * fabricated.
 */
export function excerptOf(run: RunStatus) {
  const firstEditable = run.document.sections.find((section) => !section.locked) || run.document.sections[0];
  const source = firstEditable?.body || run.message || "";
  // Take a generous budget, then pull the cut BACK to the last sentence that fits inside it.
  const budget = previewWords(source, 70);
  const ends = [...budget.matchAll(/[.!?](?=\s|$)/g)];
  const lastEnd = ends.length ? (ends[ends.length - 1].index ?? -1) : -1;
  if (lastEnd > 0) return budget.slice(0, lastEnd + 1);
  // One sentence longer than the whole budget: cut on a word and let the note say it is an
  // excerpt. Never append an ellipsis, and never invent a closing clause.
  return previewWords(source, 45);
}

export function packageValue(run: RunStatus) {
  const pkg = run.finalCopy?.package;
  if (!pkg) return "Not exported";
  return `${plural(pkg.fileCount, "file", "files")} · ${formatBytes(pkg.bytes)}`;
}

export type LibraryQa = {
  gates: Gate[];
  counts: GateCounts;
  blockers: ReturnType<typeof gateBlockers>;
  /** The date shown on the card and in the DataList, with the term that honestly names it. */
  dateLabel: string;
  date: string;
  /** One line, on the card. Never a percentage, never a tick unless the backend passed a gate. */
  cardLine: string;
  /**
   * The card's QA line: the same verdict as `cardLine`, from the same counts, minus the date
   * (the card sets the date in its own tabular-nums column, so the dates form one vertical scan
   * line) and minus any restatement of the human decision (the StatusPill sits directly above
   * it and already carries that word). It states the MACHINE verdict, once — so an approved run
   * whose checks never ran reads "Approved" on the pill and "Final QA not run" beneath it, which
   * is the whole point. It can never print a figure `counts` did not produce.
   */
  cardState: string;
  /** The long form, in the detail. */
  sentence: string;
};

/**
 * The honest QA line. Five cases, and none of them can print a number that a backend verdict
 * did not produce. `deriveGates` never reads run.status, so an approved run does not make a
 * check pass — in local preview (apiUrl === "") every gate is correctly "not_run".
 */
export function libraryQa(run: RunStatus): LibraryQa {
  const gates = deriveGates(run, "idle");
  const counts = gateCounts(gates);
  const blockers = gateBlockers(gates);
  const date = formatDate(run.qaLoop?.completedAt || run.document.updatedAt);
  const checks = (n: number) => plural(n, "check", "checks");

  if (qaVerified(run)) {
    return {
      gates,
      counts,
      blockers,
      dateLabel: "Approved",
      date,
      cardLine: `Final QA aligned · ${counts.passed} of ${counts.total} checks passed · Approved ${date}`,
      cardState: `Final QA aligned · ${counts.passed} of ${counts.total} passed`,
      sentence: `Final QA aligned. ${counts.passed} of ${counts.total} checks passed.`
    };
  }

  if (qaRejected(run) || counts.failed > 0) {
    return {
      gates,
      counts,
      blockers,
      dateLabel: "Last QA",
      date,
      cardLine: `Final QA — blocked on ${checks(counts.failed)} · Last QA ${date}`,
      cardState: `Final QA blocked on ${checks(counts.failed)}`,
      sentence: "Final QA did not clear."
    };
  }

  if (counts.ran > 0) {
    return {
      gates,
      counts,
      blockers,
      dateLabel: "Updated",
      date,
      cardLine: `Final QA — ${counts.ran} of ${counts.total} checks run · Updated ${date}`,
      cardState: `Final QA — ${counts.ran} of ${counts.total} checks run`,
      sentence: `Final QA is incomplete. ${counts.ran} of ${counts.total} checks have a verdict.`
    };
  }

  // A human approval is on file and NOT ONE check has run. The old screen printed a bare
  // "Approved" here. This is the single most important sentence on the screen.
  if (run.status === "approved") {
    return {
      gates,
      counts,
      blockers,
      dateLabel: "Approved",
      date,
      cardLine: `Approved by a human · Final QA not run · ${date}`,
      cardState: "Final QA not run",
      sentence: `Approval recorded locally. No QA check has run — 0 of ${counts.total} have a verdict.`
    };
  }

  return {
    gates,
    counts,
    blockers,
    dateLabel: "Updated",
    date,
    cardLine: `Final QA — not run · Updated ${date}`,
    cardState: "Final QA — not run",
    sentence: `Final QA has not been run. 0 of ${counts.total} checks have a verdict.`
  };
}

/**
 * The message deriveGates() GENERATES when a level is named in qaLoop.blockedLevels and the
 * backend supplied no reason of its own (lib/qa.ts). It is boilerplate, and printing it once per
 * blocked check produced three near-identical paragraphs saying the same thing.
 *
 * The inspector states that consequence ONCE and uses this to recognise the boilerplate. A
 * message the BACKEND actually returned never matches it, so a real blocking reason is always
 * printed verbatim beside its check. Nothing real is ever collapsed away.
 */
export function genericBlockerMessage(gateLabel: string) {
  return `${gateLabel} did not clear. It needs a repair or a reviewer decision before export.`;
}

export type StepAction = {
  label: string;
  to: string;
  testId: "library-action-edit" | "library-action-review";
};

/**
 * The ONE next step for a run. Presentation only — it never links to an export, and it can
 * never become a shortcut past the QA gate. The download stays in the detail, under exactly
 * today's condition (a package exists AND apiUrl is set).
 */
export function nextStep(run: RunStatus): StepAction {
  const editTo = `/runs/${run.runId}/editor`;
  const reviewTo = `/runs/${run.runId}/review`;
  const pending = run.changes.filter((change) => change.verdict === "pending").length;

  if (run.status === "draft" || run.status === "queued" || run.status === "running") {
    return { label: "Open editor", to: editTo, testId: "library-action-edit" };
  }
  if (run.status === "blocked" || run.status === "blocked_for_human") {
    return { label: "Open blockers", to: reviewTo, testId: "library-action-review" };
  }
  if (run.status === "approved") {
    return {
      label: run.finalCopy?.package ? "Open package" : "Open final QA",
      to: reviewTo,
      testId: "library-action-review"
    };
  }
  if (pending > 0) {
    return { label: `Review ${plural(pending, "edit", "edits")}`, to: reviewTo, testId: "library-action-review" };
  }
  return { label: "Open final QA", to: reviewTo, testId: "library-action-review" };
}

/** The other route, so both library-action-edit and library-action-review always exist. */
export function alternateStep(run: RunStatus, primary: StepAction): StepAction {
  if (primary.testId === "library-action-edit") {
    return { label: "Open review", to: `/runs/${run.runId}/review`, testId: "library-action-review" };
  }
  return { label: "Open editor", to: `/runs/${run.runId}/editor`, testId: "library-action-edit" };
}

/** The words isInternalRun() matches. Named on screen, because the filter can hide a customer. */
export const INTERNAL_FILTER_WORDS = "test, smoke, QA, check, demo or sample";
